import React, { useState } from 'react';
import { FunnelMetrics, FunnelStep } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
import { ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
interface ThroughputFunnelProps {
  metrics: FunnelMetrics[];
  onStepClick: (step: FunnelStep) => void;
}
export function ThroughputFunnel({
  metrics,
  onStepClick
}: ThroughputFunnelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const maxCount = Math.max(...metrics.map(m => m.count));
  const created = metrics.find(m => m.step === 'Created')?.count || 0;
  const funded = metrics.find(m => m.step === 'Funded')?.count || 0;
  const pullThrough = created > 0 ? (funded / created * 100).toFixed(1) : '0';
  return <Card className="mb-6 p-0 overflow-hidden" noPadding data-id="element-249">
      <CardHeader bordered className="!mb-0" data-id="element-250">
        <div className="flex items-center gap-4" data-id="element-251">
          <CardTitle data-id="element-252">Throughput Funnel</CardTitle>
          <AnimatePresence data-id="element-253">
            {isCollapsed && <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} className="flex items-center gap-2 text-sm text-[#706E6B]" data-id="element-254">
                <span className="font-medium text-[#181818]" data-id="element-255">
                  {created.toLocaleString()}
                </span>
                <span data-id="element-256">Created</span>
                <ArrowDown className="h-3 w-3 text-[#DDDBDA] rotate-[-90deg]" data-id="element-257" />
                <span className="font-medium text-[#04844B]" data-id="element-258">
                  {funded.toLocaleString()}
                </span>
                <span data-id="element-259">Funded</span>
                <span className="text-[#04844B] font-medium" data-id="element-260">
                  ({pullThrough}%)
                </span>
              </motion.div>}
          </AnimatePresence>
        </div>
        <button onClick={() => setIsCollapsed(!isCollapsed)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#706E6B] hover:text-[#181818] hover:bg-white rounded transition-colors" aria-expanded={!isCollapsed} aria-controls="funnel-content" data-id="element-261">
          {isCollapsed ? <>
              <span data-id="element-262">Expand</span>
              <ChevronDown className="h-4 w-4" data-id="element-263" />
            </> : <>
              <span data-id="element-264">Collapse</span>
              <ChevronUp className="h-4 w-4" data-id="element-265" />
            </>}
        </button>
      </CardHeader>

      <AnimatePresence initial={false} data-id="element-266">
        {!isCollapsed && <motion.div id="funnel-content" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.4, 0, 0.2, 1]
      }} className="overflow-hidden" data-id="element-267">
            <div className="flex flex-col items-center max-w-2xl mx-auto px-5 py-6" data-id="element-268">
              {metrics.map((metric, index) => {
            const isLast = index === metrics.length - 1;
            const isFirst = index === 0;
            const widthPercentage = Math.max(metric.count / maxCount * 100, 25);
            const isFunded = metric.step === 'Funded';
            return <motion.div key={metric.step} initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.04
            }} className="w-full flex flex-col items-center" data-id="element-269">
                    {/* Step Bar */}
                    <button className="relative h-14 transition-all duration-200 cursor-pointer group" style={{
                width: `${widthPercentage}%`
              }} onClick={() => onStepClick(metric.step)} data-id="element-270">
                      <div className={clsx('absolute inset-0 rounded-lg flex items-center justify-between px-5 border transition-all duration-200', isFunded ? 'bg-[#04844B] border-[#04844B] text-white shadow-sm' : 'bg-white border-[#E5E5E5] group-hover:border-[#0176D3] group-hover:shadow-md')} data-id="element-271">
                        <span className={clsx('text-sm font-medium truncate', isFunded ? 'text-white' : 'text-[#181818]')} data-id="element-272">
                          {metric.step}
                        </span>
                        <div className="flex items-baseline gap-2" data-id="element-273">
                          <span className={clsx('text-xl font-bold tabular-nums', isFunded ? 'text-white' : 'text-[#181818]')} data-id="element-274">
                            {metric.count.toLocaleString()}
                          </span>
                          {!isFirst && <span className={clsx('text-xs font-medium', isFunded ? 'text-white/80' : 'text-[#706E6B]')} data-id="element-275">
                              ({metric.overallConversionRate.toFixed(0)}%)
                            </span>}
                        </div>
                      </div>
                    </button>

                    {/* Connector */}
                    {!isLast && <div className="h-8 flex items-center justify-center relative w-full" data-id="element-276">
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#E5E5E5] -translate-x-1/2" data-id="element-277" />
                        <div className="z-10 bg-[#FAFAFA] px-2.5 py-1 rounded-full text-xs font-medium text-[#706E6B] border border-[#E5E5E5] flex items-center gap-1" data-id="element-278">
                          <ArrowDown className="h-3 w-3" data-id="element-279" />
                          {metrics[index + 1].conversionRate.toFixed(0)}%
                        </div>
                      </div>}
                  </motion.div>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </Card>;
}